import { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Toast, useToast } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  DatarouterError,
  useRentalManagerRenterContactWebApiHandler_getTenants,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/rental-manager-env';
import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { logError } from '@zg-rentals/log-error';

import { createZgApiHttpClient } from '../../../client/zgApiHttpClient';

import { useSelector } from '../../selectors/useSelector';

import TenantDirectoryCSEvents from '../../model/ClickstreamEvent/TenantDirectoryCSEvents';

import ConnectedModuleCard from '../../components/ConnectedModuleCard';
import EmptyTenantsCard from './components/EmptyTenantsCard';

import { StyledNoUnderlineAnchor } from '../../StyledComponents/StyledComponents';
import TenantAccordionList from './components/TenantAccordionList';

import type {
  RentalManagerRenterUpdateRenterResponseDto,
  RenterInfoRenterAddedDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const TOAST_MESSAGE = {
  SUCCESS_TENANCY_PERIOD_REMOVE: 'Tenancy has been successfully removed',

  SUCCESS_TENANCY_PERIOD_EDIT: 'Tenancy has been successfully edited',
  SUCCESS_TENANCY_PERIOD_CREATE: 'Tenancy has been successfully created',
  SUCCESS_REMOVE: 'The tenant has been successfully removed',
  SUCCESS_EDIT: 'Tenant details edited successfully',
} as const;

const TenantsModule = () => {
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const { currentProperty, env, listingAlias, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      env: state.env,
      user: state.user,
      listingAlias: state.properties.currentProperty.listingAlias,
    }),
    shallowEqual,
  );

  const [isModalTenantSelectOpen, setIsModalTenantSelectOpen] = useState(false);

  const {
    data: getTenantsData,
    isFetching,
    isPending,
    isError,
    error,
  } = useRentalManagerRenterContactWebApiHandler_getTenants({
    listingAlias,
  });

  const { data: webAnalyticsDatWithLease } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: true,
    },
    {
      enabled: Boolean(listingAlias),
    },
  );

  // using same client as in client/index.js
  const client = createZgApiHttpClient(env.zgApiPublic);

  const isEmptyTenants = getTenantsData?.tenants.length === 0;

  useEffect(() => {
    if (isError) {
      // error
      // console.log('>> ERROR: ', { error });
    }
  }, [isError, error]);

  const invalidateGetTenantsQuery = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerRenterContactWebApiHandler_getTenants.getKey({ listingAlias }),
    });
  };

  const triggerCSEventAddTenantClick = () => {
    TenantDirectoryCSEvents.TenantsModuleAddTenantClick({
      currentProperty,
      user,
      triggerLocation: TenantDirectoryCSEvents.CS_EVENT_TRIGGER_LOCATION.ZRM_PROPERTY_OVERVIEW,
    });
  };

  function handleAddTenantClick() {
    setIsModalTenantSelectOpen(true);
    triggerCSEventAddTenantClick();
  }

  const handleDeleteTenancyPeriodSuccess = () => {
    invalidateGetTenantsQuery();

    enqueueToast(<Toast tone="success">{TOAST_MESSAGE.SUCCESS_TENANCY_PERIOD_REMOVE}</Toast>);
  };

  const handleDeleteTenantSuccess = () => {
    invalidateGetTenantsQuery();

    enqueueToast(<Toast tone="success">{TOAST_MESSAGE.SUCCESS_REMOVE}</Toast>);
  };

  const handleDeleteTenantError = (error: DatarouterError) => {
    enqueueToast(<Toast tone="critical">{error.message}</Toast>);
  };

  const handleEditTenantSuccess = (_updatedRenterDto?: RentalManagerRenterUpdateRenterResponseDto) => {
    invalidateGetTenantsQuery();

    enqueueToast(<Toast tone="success">{TOAST_MESSAGE.SUCCESS_EDIT}</Toast>);
  };

  const handleLeaseTermActionSuccess = ({
    addedRenters,
    isEditMode,
  }: {
    addedRenters?: Array<RenterInfoRenterAddedDto>;
    isEditMode: boolean;
  }) => {
    invalidateGetTenantsQuery();

    enqueueToast(
      <Toast tone="success">
        {isEditMode ? TOAST_MESSAGE.SUCCESS_TENANCY_PERIOD_EDIT : TOAST_MESSAGE.SUCCESS_TENANCY_PERIOD_CREATE}
      </Toast>,
    );

    if (webAnalyticsDatWithLease) {
      const { lease, ...webAnalyticsData } = webAnalyticsDatWithLease;

      if (!isEditMode) {
        TenantDirectoryCSEvents.TenancyPeriodCreated({
          currentProperty: webAnalyticsData ?? currentProperty,
          lease,
          user,
        });
      } else {
        TenantDirectoryCSEvents.TenancyPeriodEditSuccess({
          currentProperty: webAnalyticsData ?? currentProperty,
          lease,
          user,
        });
      }
    }
  };

  const handleRequestProofSuccess = (message: string) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">{message}</Toast>);
  };

  const handleRequestProofError = (message: string) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="critical">{message}</Toast>);
  };

  const renderCTA = useMemo(() => {
    return (
      <StyledNoUnderlineAnchor
        data-eventid={TenantDirectoryCSEvents.TenantsModuleAddTenantClick.data_event_id}
        onClick={handleAddTenantClick}
      >
        Add tenant
      </StyledNoUnderlineAnchor>
    );
  }, []);

  return (
    <>
      <ConnectedModuleCard
        data-testid="TenantsModule"
        title="Tenants"
        ctaComponent={isPending === false && renderCTA}
        ctaFontColor="text.subtle"
        ctaFontType="body-sm"
        justifyHeadingContent={'center'}
        isLoading={isPending || isFetching}
      >
        {/**
         * TenantAccordionList needs to be removed and replaced in the DOM
         * after the query is fetched in order to keep the index in sync with the tenants array
         *
         * The child component Accordion.Item open and closed state is controlled by value
         * If the component is not removed, the index will not be updated and the accordion will not open and close correctly
         */}
        {isPending === false && isFetching === false && (
          <>
            {isEmptyTenants ? (
              <EmptyTenantsCard />
            ) : (
              <TenantAccordionList
                tenants={getTenantsData?.tenants || []}
                onDeleteTenancyPeriodSuccess={handleDeleteTenancyPeriodSuccess}
                onDeleteTenantSuccess={handleDeleteTenantSuccess}
                onDeleteTenantError={handleDeleteTenantError}
                onEditTenantSuccess={handleEditTenantSuccess}
                onLeaseTermActionSuccess={handleLeaseTermActionSuccess}
                onRequestProofSuccess={handleRequestProofSuccess}
                onRequestProofError={handleRequestProofError}
              />
            )}
          </>
        )}
      </ConnectedModuleCard>

      <ModalTenantSelect
        client={client}
        listingAlias={listingAlias}
        isOpen={isModalTenantSelectOpen}
        onAddRenterSuccess={() => {
          if (currentProperty && user) {
            TenantDirectoryCSEvents.TenantsModuleSaveClick({
              triggerLocation: TenantDirectoryCSEvents.CS_EVENT_TRIGGER_LOCATION.ZRM_PROPERTY_OVERVIEW,
              currentProperty,
              user,
            });
          } else {
            logError({
              error: new Error(
                'Unable to fire Clickstream event_type_id: ' +
                  TenantDirectoryCSEvents.TenantsModuleSaveClick.data_event_id,
              ),
              errorType: 'AnalyticsError',
              context: {
                triggerLocation: TenantDirectoryCSEvents.CS_EVENT_TRIGGER_LOCATION.ZRM_PROPERTY_OVERVIEW,
                currentProperty,
                user,
              },
            });
          }
        }}
        queryClient={queryClient}
        onClose={() => setIsModalTenantSelectOpen(false)}
        onPrimaryCTAClick={() => {
          invalidateGetTenantsQuery();
          setIsModalTenantSelectOpen(false);
        }}
      />
    </>
  );
};

export default TenantsModule;
